import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';
import { AsyncImage } from 'loadable-image';

import { Icon as CroplerIcon } from '@cropler/ui-kit';

import styles from './styles.module.scss';

import { selectAddingSeason, selectMainSensor, selectMainSensorPeriod } from '../../store/slices/sensors';
import { MapLat, MapLng, MinDifferenceGpsAndInstall } from '../../constants/keys';
import { PathnamesMap } from '../../constants/ui';
import { BatteryLevel, CropName, NetworkType } from '../GlobalComponents/index.jsx';
import { isDateInPeriod } from '../../utils/dates';
import { CardDownloadImage, CardMetric, CardNotes } from './index.jsx';
import { DateFormat, DateLLFormat, TimeFormat } from '../../constants/data';
import { resizeImageCard } from '../../utils/images';
import { AiAnalyticsLabel, AiAnalyticsNumber } from '../AiAnalytics/index.jsx';
import { getMetricsLabels } from '../../utils/metrics';

const Card = ({
  t,
  name,
  image,
  crop,
  location,
  bbchAI,
  temperature,
  gdd,
  dewDuration,
  ndvi,
  installDate,
  removalDate,
  pointsDelta,
  soilMoisture,
  soilTemperature,
  date,
  isAiEnable,
  isNoData,
  showDownloadButton,
  notesLength = 0,
  onClick,
  onClickSync,
  onClickNotes,
  onClickExpandImage,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUserRole } = useSelector((state) => state.auth);
  const today = new Date();
  const todayFormat = dayjs().format(DateFormat);
  const lastSyncFormat = location ? dayjs(location.sensor.synced_at).format(DateFormat) : null;
  const MetricsLabels = getMetricsLabels(t);

  const [soilMoistureValue, setSoilMoistureValue] = useState(null);
  const [soilTemperatureValue, setSoilTemperatureValue] = useState(null);

  const { selectedDate } = useSelector((state) => state.sensors);

  const onClickAddSeason = () => {
    const coordinates =
      location.sensor && location.sensor.gps_point
        ? location.sensor.gps_point.coordinates
        : location.gps_point
          ? location.gps_point.coordinates
          : location.install_point.coordinates;
    localStorage.setItem(MapLng, coordinates[0]);
    localStorage.setItem(MapLat, coordinates[1]);
    dispatch(selectAddingSeason(true));
    dispatch(selectMainSensor({ id: location.id }));
    if (installDate && removalDate) {
      dispatch(selectMainSensorPeriod({ period: [installDate, removalDate] }));
    }
    navigate(`/${PathnamesMap.fields}`);
  };

  useEffect(() => {
    if (soilMoisture && soilMoisture.length > 0) {
      let moisture = null;
      soilMoisture.map((item) => {
        if (item !== null && moisture === null) {
          moisture = item.avg;
        }
      });
      setSoilMoistureValue(moisture);
    }

    if (soilTemperature && soilTemperature.length > 0) {
      let soilTemp = null;
      soilTemperature.map((item) => {
        if (item !== null && soilTemp === null) {
          soilTemp = item.avg;
        }
      });
      setSoilTemperatureValue(soilTemp);
    }
  }, [soilMoisture, soilTemperature]);

  return (
    <div className={styles.card}>
      <div className={`${styles.cardImage}`}>
        {isNoData ? (
          <div className={styles.cardImageWrapper}>
            <CroplerIcon name="cropler" />
          </div>
        ) : (
          <button className={styles.cardImageWrapper} onClick={onClick}>
            {image ? (
              <AsyncImage src={resizeImageCard(image)} loader={<div className="skeleton-bg"></div>} timeout={500} />
            ) : (
              <CroplerIcon name="cropler" />
            )}
          </button>
        )}

        <div className={styles.cardPanelRow}>
          {/*Sensor name*/}
          {/*{name && (*/}
          {/*  <div className={styles.cardTextLabel}>*/}
          {/*    <CameraIcon />*/}
          {/*    {name}*/}
          {/*  </div>*/}
          {/*)}*/}
          {/*Gps*/}
          {(currentUserRole === 'owner' || currentUserRole === 'editor') &&
            (isDateInPeriod(installDate, removalDate, today) || dayjs(installDate).isAfter(today)) &&
            !!pointsDelta &&
            pointsDelta > MinDifferenceGpsAndInstall && (
              <div className={`${styles.cardPanel} ${styles.cardPanelGps}`}>
                {t('card.gpsMismatchDetected')}
                <Button type="primary" onClick={onClickSync}>
                  {t('card.sync')}
                </Button>
              </div>
            )}
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardRow}>
          <div className={styles.cardCell}>
            {/*Crop*/}
            {!date && (
              <div className={styles.cardProperties}>
                <span className={styles.cardPropertiesItem}>
                  <span className={'text-overflow'}>
                    {location && location.fieldName ? location.fieldName : t('card.unknownField')}
                  </span>
                </span>
                {crop && (
                  <span className={styles.cardPropertiesItem}>
                    <CropName t={t} cropId={crop} />
                  </span>
                )}
              </div>
            )}
            {date && (
              <time className={styles.cardTime}>
                <CroplerIcon name="time" />
                {dayjs(date).format(TimeFormat)}
              </time>
            )}
            {/*Name*/}
            {name && (
              <p className={styles.cardName}>{location && location.name ? location.name : t('card.unknownLocation')}</p>
            )}
            {date && <p className={styles.cardName}>{dayjs(date).format(DateLLFormat)}</p>}
          </div>

          <div className={styles.cardCell}>
            {/*BBCH*/}
            {bbchAI && (
              <div className={`${styles.cardAi}`}>
                BBCH
                <AiAnalyticsNumber number={bbchAI.value} />
              </div>
            )}

            {!date && !crop && (currentUserRole === 'owner' || currentUserRole === 'editor') && (
              <Button
                className={styles.cardAddButton}
                type="secondary"
                size="small"
                icon={<CroplerIcon name="add" />}
                onClick={onClickAddSeason}
              >
                {t('card.buttonSeason')}
              </Button>
            )}

            {(showDownloadButton || notesLength > 0) && (
              <div className={styles.cardNav}>
                {notesLength > 0 && <CardNotes count={notesLength} onClickNotes={onClickNotes} />}
                {showDownloadButton && <CardDownloadImage image={{ url: image, takenAt: date }} />}
              </div>
            )}
          </div>
        </div>

        {isNoData ? (
          <div className={styles.cardGrid}>
            <CardMetric name={MetricsLabels.temperature} t={t} />
            <CardMetric name={MetricsLabels.gdd} t={t} />
            <CardMetric name={MetricsLabels.dewfallDuration} t={t} />
            <CardMetric name={MetricsLabels.ndvi} t={t} />
          </div>
        ) : (
          <div className={styles.cardGrid}>
            <CardMetric name={MetricsLabels.temperature} value={temperature} t={t} />
            <CardMetric name={MetricsLabels.gdd} value={gdd} t={t} />
            <CardMetric name={MetricsLabels.dewfallDuration} value={dewDuration} isDewDuration={true} t={t} />
            <CardMetric
              name={MetricsLabels.ndvi}
              value={ndvi ? ndvi.value : ''}
              valueIncreasing={ndvi ? ndvi.increasing : ''}
              t={t}
            />
            {((soilMoisture && soilMoisture.length > 0) || (soilTemperature && soilTemperature.length > 0)) && (
              <>
                <CardMetric
                  name={MetricsLabels.soilMoisture}
                  value={soilMoistureValue !== null ? soilMoistureValue : t('empty.noData')}
                  t={t}
                />
                <CardMetric
                  name={MetricsLabels.soilTemperature}
                  value={
                    soilTemperatureValue !== null ? Math.round(soilTemperatureValue * 100) / 100 : t('empty.noData')
                  }
                  t={t}
                />
              </>
            )}
          </div>
        )}
      </div>

      {todayFormat && todayFormat === selectedDate && (
        <div className={`${styles.cardTopNav} ${styles.cardTopNavLeft}`}>
          {/*Battery*/}
          {lastSyncFormat === todayFormat && location && location.sensor && (
            <div className={styles.cardBattery}>
              <BatteryLevel t={t} value={location.sensor.battery_level} isIcon={true} />
            </div>
          )}
          {/*Network*/}
          {location && location.sensor && (
            <div
              className={`
              ${styles.cardNetwork} 
              ${!location.sensor.network_type || lastSyncFormat !== todayFormat ? styles.cardNetworkDisabled : ''}
              `}
            >
              <NetworkType
                value={lastSyncFormat === todayFormat ? location.sensor.network_type : null}
                level={lastSyncFormat === todayFormat ? location.sensor.signal_level : null}
                t={t}
              />
            </div>
          )}
        </div>
      )}

      <div className={styles.cardTopNav}>
        {/* AI */}
        {isAiEnable && (
          <div className={styles.cardLabel}>
            <AiAnalyticsLabel hideTooltip={true} />
          </div>
        )}
        {/* Show full photo */}
        {image && (
          <Button type="primary" size="small" onClick={onClickExpandImage} icon={<CroplerIcon name="expand" />} />
        )}
      </div>
    </div>
  );
};

export default Card;
